import PageErrorBoundary from '@/components/core/common/ErrorBoundary/PageErrorBoundary';
import SplashScreen from '@/components/generic/SplashScreen';
import { lazy, Suspense } from 'react';
import { routes } from '@/pages/routes';
import { AppRoutes } from '@/pages/routes-config';
import { Toaster } from '@workspace/ui/components/sonner';
import { OfflineNotice } from '@workspace/ui/components/offline-notice';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Config } from './utils/config';
import { QueryProvider } from './providers/QueryProvider';
import HelpLauncher from './components/generic/HelpLauncher';
import ExclusiveOfferHost from './components/generic/ExclusiveOfferHost';
import { InsufficientCreditsModal } from './components/billing/InsufficientCreditsModal';
import { TopUpCreditsModal } from './components/billing/TopUpCreditsModal';
import PlansUpgradeModal from './components/core/common/Modals/PlansUpgradeModal';

const GuestApp = lazy(() => import('@/guest/GuestApp'));
const AuthLayout = lazy(() => import('@/Layout/AuthLayout'));
// Keeps top-positioned toasts clear of the builder header.
const TOAST_TOP_OFFSET = 'calc(var(--header-height) + 12px)';

const RootLayout = lazy(() => import('@/Layout/RootLayout'));
const ViewLayout = lazy(() => import('@/Layout/ViewLayout'));
const PageNotFound = lazy(() => import('@/pages/page-not-found'));
const PasswordDocumentRecipientView = lazy(
  () => import('@/pages/password-document-recipient-view'),
);
const ProposalNotFound = lazy(() => import('@/pages/proposal-not-found'));
const DocumentExpired = lazy(() => import('@/pages/document-expired'));
const FigmaServicesPreviewPage = lazy(() => import('@/pages/figma-services-preview'));
const ViewPage = lazy(() => import('@/pages/view'));

export function App() {
  const runType = Config.APP_RUN_TYPE;
  const isBuilderDomain = runType ? runType === 'builder' : false;
  const suspenseFallback = null;

  if (runType === 'guest') {
    // Router-free fallback: SplashScreen calls useLocation() and GuestApp owns the Router.
    return (
      <Suspense fallback={null}>
        <GuestApp />
      </Suspense>
    );
  }

  if (!isBuilderDomain) {
    return (
      <QueryProvider>
        <BrowserRouter>
          <SplashScreen />
          <Routes>
            {/* Standalone routes without layout **/}
            <Route
              path={AppRoutes.proposalNotFound()}
              element={
                <Suspense fallback={suspenseFallback}>
                  <ProposalNotFound />
                </Suspense>
              }
            />
            <Route
              path={AppRoutes.passwordDocumentRecipientView()}
              element={
                <Suspense fallback={suspenseFallback}>
                  <PasswordDocumentRecipientView />
                </Suspense>
              }
            />
            <Route
              path={AppRoutes.figmaServicesPreview()}
              element={
                <Suspense fallback={suspenseFallback}>
                  <FigmaServicesPreviewPage />
                </Suspense>
              }
            />
            <Route
              path={AppRoutes.documentExpired()}
              element={
                <Suspense fallback={suspenseFallback}>
                  <DocumentExpired />
                </Suspense>
              }
            />

            {/* Routes with layout **/}
            <Route
              path="/"
              element={
                <Suspense fallback={suspenseFallback}>
                  <ViewLayout />
                </Suspense>
              }
            >
              <Route
                path={AppRoutes.share()}
                element={
                  <Suspense fallback={suspenseFallback}>
                    <ViewPage />
                  </Suspense>
                }
              />
              <Route
                path="*"
                element={
                  <Suspense fallback={suspenseFallback}>
                    <PageNotFound />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
          <HelpLauncher />
          <Toaster
            expand={true}
            offset={{ top: TOAST_TOP_OFFSET }}
            mobileOffset={{ top: TOAST_TOP_OFFSET }}
          />
          <OfflineNotice />
        </BrowserRouter>
      </QueryProvider>
    );
  }

  return (
    <QueryProvider>
      <BrowserRouter>
        <SplashScreen />
        <Routes>
          {/* Standalone routes without layout */}
          <Route
            path={AppRoutes.proposalNotFound()}
            element={
              <Suspense fallback={suspenseFallback}>
                <ProposalNotFound />
              </Suspense>
            }
          />
          <Route
            path={AppRoutes.passwordDocumentRecipientView()}
            element={
              <Suspense fallback={suspenseFallback}>
                <PasswordDocumentRecipientView />
              </Suspense>
            }
          />
          <Route
            path={AppRoutes.figmaServicesPreview()}
            element={
              <Suspense fallback={suspenseFallback}>
                <FigmaServicesPreviewPage />
              </Suspense>
            }
          />
          <Route
            path={AppRoutes.documentExpired()}
            element={
              <Suspense fallback={suspenseFallback}>
                <DocumentExpired />
              </Suspense>
            }
          />

          <Route
            path="/"
            element={
              <Suspense fallback={suspenseFallback}>
                <AuthLayout>
                  <RootLayout />
                </AuthLayout>
              </Suspense>
            }
          >
            {routes.map((route, index) => {
              const { Component, ...rest } = route;
              return (
                <Route
                  key={index}
                  {...rest}
                  element={
                    <PageErrorBoundary>
                      <Suspense fallback={suspenseFallback}>
                        <Component />
                      </Suspense>
                    </PageErrorBoundary>
                  }
                />
              );
            })}
            <Route
              path="*"
              element={
                <Suspense fallback={suspenseFallback}>
                  <PageNotFound />
                </Suspense>
              }
            />
          </Route>
        </Routes>
        <HelpLauncher />
        <ExclusiveOfferHost />
        <InsufficientCreditsModal />
        <TopUpCreditsModal />
        {/* Opened from the builder and the agent page (low-credit Upgrade, collaborators panel). */}
        <PlansUpgradeModal />
        <Toaster
          expand={true}
          offset={{ top: TOAST_TOP_OFFSET }}
          mobileOffset={{ top: TOAST_TOP_OFFSET }}
        />
      </BrowserRouter>
    </QueryProvider>
  );
}
